import { useEffect, useMemo, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Textarea } from '@artosera/ui';
import {
  clesPresentation,
  useActionSelection,
  type ChargePresentation,
} from '../../api/presentation.js';
import { useQuery } from '@tanstack/react-query';
import { appelDeRequete } from '../../api/cles.js';
import { creerSourcePresentation } from './horsLigne/source.js';
import { useContacts } from '../../api/contacts.js';
import { useOeuvres } from '../../api/oeuvres.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import { PreparationSelection } from './horsLigne/PreparationPresentation.js';
import styles from './Presentation.module.css';

export function PresentationDetailPage() {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie, compte } = useShellContext();
  const { id = '' } = useParams();
  const peutLire =
    galerieAModule(galerie, 'rendez-vous-client') && galerieALaction(galerie, 'presentation:read');
  const source = useMemo(
    () => creerSourcePresentation({ compteId: compte.id, galerieSlug }),
    [compte.id, galerieSlug],
  );
  useEffect(() => () => source.liberer(), [source]);
  const charge = useQuery({
    queryKey: [...clesPresentation.charge(galerieSlug, id), 'detail'],
    queryFn: (contexte) => source.charger(galerieSlug, id, appelDeRequete(contexte)),
    enabled: peutLire,
    networkMode: 'always',
    staleTime: 0,
    gcTime: 0,
  });
  const [revision, setRevision] = useState(0);
  if (!peutLire) return <p role="alert">{t('interdit')}</p>;
  return (
    <div className={styles.page}>
      <Link className={styles.lien} to={`/${galerieSlug}/presentation`}>
        {t('retour')}
      </Link>
      {charge.isPending ? <p role="status">{t('chargement')}</p> : null}
      <ErreurPresentation erreur={charge.error} recharger={() => void charge.refetch()} />
      {charge.data ? (
        <EditionSelection
          key={`${compte.id}:${galerieSlug}:${id}:${revision}`}
          charge={charge.data}
          recharger={() => {
            void charge.refetch().then(() => setRevision((valeur) => valeur + 1));
          }}
        />
      ) : null}
    </div>
  );
}
function EditionSelection({
  charge,
  recharger,
}: {
  charge: ChargePresentation;
  recharger: () => void;
}) {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie } = useShellContext();
  const { selection } = charge;
  const peutEcrire = navigator.onLine && galerieALaction(galerie, 'presentation:write');
  const [titre, setTitre] = useState(selection.titre);
  const [note, setNote] = useState(selection.note);
  const [contactId, setContactId] = useState(selection.contactId ?? 'aucun');
  const [rechercheContact, setRechercheContact] = useState('');
  const [rechercheOeuvre, setRechercheOeuvre] = useState('');
  const [oeuvreId, setOeuvreId] = useState('');
  const [succes, setSucces] = useState('');
  const glissee = useRef<number | null>(null);
  const boutons = useRef(new Map<string, HTMLButtonElement>());
  const focusAttendu = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (focusAttendu.current) boutons.current.get(focusAttendu.current)?.focus();
    focusAttendu.current = null;
  }, [selection.version]);
  const action = useActionSelection(galerieSlug);
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheContact, parPage: 200 },
    { enabled: galerieALaction(galerie, 'contacts:read') },
  );
  const oeuvres = useOeuvres(
    galerieSlug,
    { q: rechercheOeuvre, parPage: 200 },
    { enabled: peutEcrire && galerieALaction(galerie, 'artworks:read') },
  );
  const optionsContacts = (contacts.data?.contacts ?? []).map((contact) => ({
    value: contact.id,
    label: contact.nomAffiche,
  }));
  if (contactId !== 'aucun' && !optionsContacts.some((contact) => contact.value === contactId))
    optionsContacts.push({ value: contactId, label: contactId });
  const disponibles = (oeuvres.data?.oeuvres ?? []).filter(
    (oeuvre) =>
      !selection.elements.some(
        (element) => element.oeuvreId === oeuvre.id && element.exemplaireId === null,
      ),
  );
  function deplacer(depart: number, arrivee: number, focus?: string) {
    if (
      action.isPending ||
      depart === arrivee ||
      arrivee < 0 ||
      arrivee >= selection.elements.length
    )
      return;
    const elements = [...selection.elements];
    const [element] = elements.splice(depart, 1);
    if (!element) return;
    elements.splice(arrivee, 0, element);
    setSucces('');
    focusAttendu.current = focus ?? null;
    action.mutate(
      { type: 'elements', id: selection.id, version: selection.version, elements },
      {
        onSuccess: () => {
          setSucces(t('ordreModifie'));
        },
      },
    );
  }
  return (
    <>
      <header className={styles.actions}>
        <h1>{selection.titre}</h1>
        <Link
          className={styles.lien}
          to={`/${galerieSlug}/presentation/${selection.id}/plein-ecran`}
        >
          {t('presenter')}
        </Link>
      </header>
      <PreparationSelection selectionId={selection.id} />
      <form
        className={styles.formulaire}
        onSubmit={(event) => {
          event.preventDefault();
          if (!peutEcrire || !titre.trim() || action.isPending) return;
          setSucces('');
          action.mutate(
            {
              type: 'modifier',
              id: selection.id,
              corps: {
                titre: titre.trim(),
                note,
                contactId: contactId === 'aucun' ? null : contactId,
                version: selection.version,
              },
            },
            { onSuccess: () => setSucces(t('succes')) },
          );
        }}
      >
        <label className={styles.champ}>
          {t('titreSelection')}
          <Input
            required
            maxLength={200}
            value={titre}
            disabled={!peutEcrire || action.isPending}
            onChange={(event) => setTitre(event.target.value)}
          />
        </label>
        {galerieALaction(galerie, 'contacts:read') ? (
          <>
            <label className={styles.champ}>
              {t('rechercheContact')}
              <Input
                value={rechercheContact}
                disabled={!peutEcrire}
                onChange={(event) => setRechercheContact(event.target.value)}
              />
            </label>
            <Select
              aria-label={t('rechercheContact')}
              disabled={!peutEcrire || action.isPending}
              value={contactId}
              onValueChange={setContactId}
              options={[{ value: 'aucun', label: t('sansContact') }, ...optionsContacts]}
            />
          </>
        ) : (
          <p>{selection.contactId ?? t('sansContact')}</p>
        )}
        <label className={styles.champ}>
          {t('note')}
          <Textarea
            maxLength={5000}
            value={note}
            disabled={!peutEcrire || action.isPending}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        {peutEcrire ? (
          <Button type="submit" loading={action.isPending}>
            {t('enregistrer')}
          </Button>
        ) : null}
      </form>
      <ErreurPresentation
        erreur={action.error ?? contacts.error ?? oeuvres.error}
        recharger={recharger}
      />
      <p role="status" aria-live="polite">
        {succes}
      </p>
      <section aria-labelledby="ordre-selection" className={styles.page}>
        <h2 id="ordre-selection">{t('ordre')}</h2>
        {peutEcrire ? <p className={styles.aide}>{t('glisser')}</p> : null}
        <ol className={styles.liste}>
          {selection.elements.map((element, index) => {
            const cle = `${element.oeuvreId}:${element.exemplaireId ?? ''}`;
            const presente = charge.elements.find(
              (oeuvre) =>
                oeuvre.oeuvreId === element.oeuvreId &&
                oeuvre.exemplaireId === element.exemplaireId,
            );
            const libelle = presente
              ? [presente.oeuvre.titre, presente.exemplaire?.numero].filter(Boolean).join(' — ')
              : element.oeuvreId;
            return (
              <li
                className={styles.carte}
                key={cle}
                draggable={peutEcrire && !action.isPending}
                onDragStart={(event) => {
                  glissee.current = index;
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', cle);
                }}
                onDragEnd={() => {
                  glissee.current = null;
                }}
                onDragOver={(event) => {
                  if (peutEcrire) event.preventDefault();
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (peutEcrire && glissee.current !== null) deplacer(glissee.current, index);
                  glissee.current = null;
                }}
              >
                <div className={styles.actions}>
                  <span>{libelle}</span>
                  {peutEcrire ? (
                    <>
                      <Button
                        ref={(bouton) => {
                          if (bouton) boutons.current.set(`${cle}:monter`, bouton);
                          else boutons.current.delete(`${cle}:monter`);
                        }}
                        variant="secondary"
                        aria-label={t('monter', { titre: libelle })}
                        disabled={index === 0 || action.isPending}
                        onClick={() => deplacer(index, index - 1, `${cle}:descendre`)}
                      >
                        {t('monter', { titre: libelle })}
                      </Button>
                      <Button
                        ref={(bouton) => {
                          if (bouton) boutons.current.set(`${cle}:descendre`, bouton);
                          else boutons.current.delete(`${cle}:descendre`);
                        }}
                        variant="secondary"
                        aria-label={t('descendre', { titre: libelle })}
                        disabled={index === selection.elements.length - 1 || action.isPending}
                        onClick={() => deplacer(index, index + 1, `${cle}:monter`)}
                      >
                        {t('descendre', { titre: libelle })}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={action.isPending}
                        onClick={() =>
                          action.mutate({
                            type: 'elements',
                            id: selection.id,
                            version: selection.version,
                            elements: selection.elements.filter(
                              (_, position) => position !== index,
                            ),
                          })
                        }
                      >
                        {t('retirer', { titre: libelle })}
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
        {peutEcrire && galerieALaction(galerie, 'artworks:read') ? (
          <div className={styles.actions}>
            <label className={styles.champ}>
              {t('rechercheOeuvre')}
              <Input
                value={rechercheOeuvre}
                onChange={(event) => setRechercheOeuvre(event.target.value)}
              />
            </label>
            <Select
              aria-label={t('choisirOeuvre')}
              placeholder={t('choisirOeuvre')}
              value={oeuvreId}
              onValueChange={setOeuvreId}
              options={disponibles.map((oeuvre) => ({ value: oeuvre.id, label: oeuvre.titre }))}
            />
            <Button
              disabled={
                !disponibles.some((oeuvre) => oeuvre.id === oeuvreId) ||
                selection.elements.length >= 200 ||
                action.isPending
              }
              onClick={() =>
                action.mutate(
                  {
                    type: 'elements',
                    id: selection.id,
                    version: selection.version,
                    elements: [...selection.elements, { oeuvreId, exemplaireId: null }],
                  },
                  { onSuccess: () => setOeuvreId('') },
                )
              }
            >
              {t('ajouter')}
            </Button>
          </div>
        ) : null}
      </section>
    </>
  );
}
